/** Default Font Awesome icons keyed by menu_key (sidebar / menu_setting). */
export const MENU_ICONS = {
  dashboard: "fas fa-chart-pie",
  my_tasks: "fas fa-clipboard-list",
  project_management: "fas fa-folder-open",
  project_overview: "fas fa-project-diagram",
  project_overview_all: "fas fa-th-large",
  project_overview_my: "fas fa-user-clock",
  plan_management_team: "fas fa-users",
  plan_management: "fas fa-users",
  plan_management_all: "fas fa-layer-group",
  plan_management_team_a: "fas fa-user-friends",
  plan_management_team_b: "fas fa-user-friends",
  plan_management_team_c: "fas fa-user-friends",
  plan_team_all: "fas fa-layer-group",
  plan_team_a: "fas fa-user-friends",
  plan_team_b: "fas fa-user-friends",
  plan_team_c: "fas fa-user-friends",
  qa_management: "fas fa-clipboard-check",
  qa_test_case: "fas fa-list-check",
  qa_test_run: "fas fa-play-circle",
  qa_test_report: "fas fa-file-alt",
  profile: "fas fa-user-circle",
  admin_setting: "fas fa-cog",
  role_permission: "fas fa-user-shield",
  management: "fas fa-users-cog",
  menu_management: "fas fa-users-cog",
  system_setting: "fas fa-sliders-h",
  sprint_setting: "fas fa-calendar-alt",
  menu_setting_page: "fas fa-bars",
};

const MENU_ICONS_BY_PATH = {
  "/dashboard": "fas fa-chart-pie",
  "/my-tasks": "fas fa-clipboard-list",
  "/tables": "fas fa-folder-open",
  "/project-overview": "fas fa-project-diagram",
  "/project-overview/all": "fas fa-th-large",
  "/project-overview/my": "fas fa-user-clock",
  "/test-plan-team": "fas fa-users",
  "/test-plan-team/all": "fas fa-layer-group",
  "/test-plan-team/team-a": "fas fa-user-friends",
  "/test-plan-team/team-b": "fas fa-user-friends",
  "/test-plan-team/team-c": "fas fa-user-friends",
  "/plan-management": "fas fa-users",
  "/plan-management/all": "fas fa-layer-group",
  "/plan-management/team-a": "fas fa-user-friends",
  "/plan-management/team-b": "fas fa-user-friends",
  "/plan-management/team-c": "fas fa-user-friends",
  "/qa-management": "fas fa-clipboard-check",
  "/qa-management/test-case": "fas fa-list-check",
  "/qa-management/test-run": "fas fa-play-circle",
  "/qa-management/test-report": "fas fa-file-alt",
  "/profile": "fas fa-user-circle",
  "/admin-setting": "fas fa-cog",
  "/role-permission": "fas fa-user-shield",
  "/management": "fas fa-users-cog",
  "/system-setting": "fas fa-sliders-h",
  "/system-setting/sprint-setting": "fas fa-calendar-alt",
  "/system-setting/menu-setting": "fas fa-bars",
  "/system-setting/ai-prompt-setting": "fas fa-wand-magic-sparkles",
};

/** Well-known System Setting child routes (when not yet in DB). */
export const SYSTEM_SETTING_CHILDREN = [
  {
    menuKey: "sprint_setting",
    menuName: "Sprint Setting",
    routePath: "/system-setting/sprint-setting",
    sectionName: "System",
    sortOrder: 81,
    icon: "fas fa-calendar-alt",
  },
  {
    menuKey: "menu_setting_page",
    menuName: "Menu Setting",
    routePath: "/system-setting/menu-setting",
    sectionName: "System",
    sortOrder: 82,
    icon: "fas fa-bars",
  },
  {
    menuKey: "ai_prompt_setting",
    menuName: "AI Prompt Setting",
    routePath: "/system-setting/ai-prompt-setting",
    sectionName: "System",
    sortOrder: 83,
    icon: "fas fa-wand-magic-sparkles",
  },
];

export function resolveMenuIcon(m) {
  const direct = String(m?.icon ?? "").trim();
  if (direct) return direct;
  const key = String(m?.menuKey ?? m?.menu_key ?? "")
    .trim()
    .toLowerCase();
  if (key && MENU_ICONS[key]) return MENU_ICONS[key];
  const path = String(m?.routePath ?? m?.route_path ?? "").trim();
  if (path && MENU_ICONS_BY_PATH[path]) return MENU_ICONS_BY_PATH[path];
  return "fas fa-circle";
}

/** Keep one row per menu_key. Real DB ids win over synthetic; then lowest id. */
export function dedupeMenuRowsByKey(rows) {
  const byKey = new Map();
  const isReal = (row) => !row.isSynthetic && Number(row.menuId) > 0;
  for (const raw of rows || []) {
    const row = normalizeMenuRow(raw);
    const key = String(row.menuKey || row.routePath || row.menuName || "")
      .trim()
      .toLowerCase();
    if (!key) continue;
    const next = { ...raw, ...row };
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, next);
      continue;
    }
    const nextReal = isReal(next);
    const prevReal = isReal(existing);
    if (nextReal && !prevReal) {
      byKey.set(key, next);
    } else if (nextReal === prevReal && Number(next.menuId) < Number(existing.menuId)) {
      byKey.set(key, next);
    }
  }
  return Array.from(byKey.values());
}

export function normalizeMenuRow(m) {
  const row = {
    menuId: Number(m.menuId ?? m.menu_id),
    menuKey: m.menuKey ?? m.menu_key ?? null,
    menuName: m.menuName ?? m.menu_name ?? "",
    sectionName: m.sectionName ?? m.section_name ?? "",
    routePath: m.routePath ?? m.route_path ?? "",
    routeName: m.routeName ?? m.route_name ?? "",
    sortOrder: Number(m.sortOrder ?? m.sort_order ?? 0),
    parentMenuId:
      m.parentMenuId ?? m.parent_menu_id ?? m.parentId ?? null,
    isActive: !!(m.isActive ?? m.is_active ?? true),
    isSynthetic: !!m.isSynthetic,
    canView: !!m.canView,
    canCreate: !!m.canCreate,
    canEdit: !!m.canEdit,
  };
  return { ...row, icon: resolveMenuIcon({ ...row, icon: m?.icon }) };
}

/** Well-known Project Overview child routes (when not yet in DB). */
export const PROJECT_OVERVIEW_CHILDREN = [
  {
    menuKey: "project_overview_all",
    menuName: "Overview",
    routeName: "Project Overview All",
    routePath: "/project-overview/all",
    sectionName: "Main",
    sortOrder: 32,
    icon: "fas fa-th-large",
  },
  {
    menuKey: "project_overview_my",
    menuName: "My Projects",
    routeName: "Project Overview My",
    routePath: "/project-overview/my",
    sectionName: "Main",
    sortOrder: 33,
    icon: "fas fa-user-clock",
  },
  {
    menuKey: "project_management",
    menuName: "Management",
    routeName: "Tables",
    routePath: "/tables",
    sectionName: "Main",
    sortOrder: 34,
    icon: "fas fa-folder-open",
  },
];

/**
 * Ensures System Setting sub-menus exist in the list (frontend fallback).
 */
export function mergeSystemSettingChildren(rows) {
  return mergeParentChildren(rows, {
    parentMatchers: [
      (r) => r.routePath === "/system-setting",
      (r) => r.menuKey === "system_setting",
      (r) => r.menuName === "System Setting",
    ],
    children: SYSTEM_SETTING_CHILDREN,
    syntheticBaseId: -1000,
  });
}

/**
 * Ensures Project Overview sub-menus exist in the list (frontend fallback).
 */
export function mergeProjectOverviewChildren(rows) {
  return mergeParentChildren(rows, {
    parentMatchers: [
      (r) => r.routePath === "/project-overview",
      (r) => r.menuKey === "project_overview",
      (r) => r.menuName === "Project Overview",
      (r) => r.menuName === "Project Management" && r.routePath === "/project-overview",
    ],
    children: PROJECT_OVERVIEW_CHILDREN,
    syntheticBaseId: -2000,
  });
}

export const PLAN_MANAGEMENT_CHILDREN = [
  {
    menuKey: "plan_management_all",
    menuName: "Overview",
    routeName: "Plan Management All",
    routePath: "/plan-management/all",
    sectionName: "Main",
    sortOrder: 42,
    icon: "fas fa-layer-group",
  },
  {
    menuKey: "plan_management_team_a",
    menuName: "Team A",
    routeName: "Plan Management Team A",
    routePath: "/plan-management/team-a",
    sectionName: "Main",
    sortOrder: 43,
    icon: "fas fa-user-friends",
  },
  {
    menuKey: "plan_management_team_b",
    menuName: "Team B",
    routeName: "Plan Management Team B",
    routePath: "/plan-management/team-b",
    sectionName: "Main",
    sortOrder: 44,
    icon: "fas fa-user-friends",
  },
  {
    menuKey: "plan_management_team_c",
    menuName: "Team C",
    routeName: "Plan Management Team C",
    routePath: "/plan-management/team-c",
    sectionName: "Main",
    sortOrder: 45,
    icon: "fas fa-user-friends",
  },
];

export function mergePlanManagementChildren(rows) {
  return mergeParentChildren(rows, {
    parentMatchers: [
      (r) => r.routePath === "/plan-management",
      (r) => r.menuKey === "plan_management",
      (r) => r.menuName === "Plan Management" && r.routePath === "/plan-management",
    ],
    children: PLAN_MANAGEMENT_CHILDREN,
    syntheticBaseId: -4000,
  });
}

export const PLAN_MANAGEMENT_TEAM_CHILDREN = [
  {
    menuKey: "plan_team_all",
    menuName: "Overview",
    routeName: "Plan Team All",
    routePath: "/test-plan-team/all",
    sectionName: "Main",
    sortOrder: 40,
    icon: "fas fa-layer-group",
  },
  {
    menuKey: "plan_team_a",
    menuName: "Team A",
    routeName: "Plan Team A",
    routePath: "/test-plan-team/team-a",
    sectionName: "Main",
    sortOrder: 41,
    icon: "fas fa-user-friends",
  },
  {
    menuKey: "plan_team_b",
    menuName: "Team B",
    routeName: "Plan Team B",
    routePath: "/test-plan-team/team-b",
    sectionName: "Main",
    sortOrder: 42,
    icon: "fas fa-user-friends",
  },
  {
    menuKey: "plan_team_c",
    menuName: "Team C",
    routeName: "Plan Team C",
    routePath: "/test-plan-team/team-c",
    sectionName: "Main",
    sortOrder: 43,
    icon: "fas fa-user-friends",
  },
];

export function mergePlanManagementTeamChildren(rows) {
  return mergeParentChildren(rows, {
    parentMatchers: [
      (r) => r.routePath === "/test-plan-team",
      (r) => r.menuKey === "plan_management_team",
      (r) =>
        r.menuName === "Plan Management Team"
        || (r.menuName === "Plan Management"
          && (r.routePath === "/test-plan-team" || r.menuKey === "plan_management_team")),
    ],
    children: PLAN_MANAGEMENT_TEAM_CHILDREN,
    syntheticBaseId: -5000,
  });
}

/** Well-known QA Management child routes (when not yet in DB). */
export const QA_MANAGEMENT_CHILDREN = [
  {
    menuKey: "qa_test_case",
    menuName: "Test Case",
    routeName: "QA Test Case",
    routePath: "/qa-management/test-case",
    sectionName: "Main",
    sortOrder: 43,
    icon: "fas fa-list-check",
  },
  {
    menuKey: "qa_test_run",
    menuName: "Test Run",
    routeName: "QA Test Run",
    routePath: "/qa-management/test-run",
    sectionName: "Main",
    sortOrder: 44,
    icon: "fas fa-play-circle",
  },
  {
    menuKey: "qa_test_report",
    menuName: "Test Report",
    routeName: "QA Test Report",
    routePath: "/qa-management/test-report",
    sectionName: "Main",
    sortOrder: 45,
    icon: "fas fa-file-alt",
  },
];

/**
 * Ensures QA Management sub-menus exist in the list (frontend fallback).
 */
export function mergeQaManagementChildren(rows) {
  return mergeParentChildren(rows, {
    parentMatchers: [
      (r) => r.routePath === "/qa-management",
      (r) => r.menuKey === "qa_management",
      (r) => r.menuName === "QA Management",
    ],
    children: QA_MANAGEMENT_CHILDREN,
    syntheticBaseId: -3000,
  });
}

/** Merge known submenu fallbacks used by Role Permission / Menu Setting UIs. */
export function mergeKnownMenuChildren(rows) {
  return mergeQaManagementChildren(
    mergePlanManagementTeamChildren(
      mergeProjectOverviewChildren(mergeSystemSettingChildren(rows)),
    ),
  );
}

function mergeParentChildren(rows, { parentMatchers, children, syntheticBaseId }) {
  const result = dedupeMenuRowsByKey(rows);
  const parent = result.find((r) => parentMatchers.some((fn) => fn(r)));
  const parentId = parent?.menuId ?? null;

  children.forEach((child, i) => {
    const exists = result.some(
      (r) =>
        r.routePath === child.routePath || r.menuKey === child.menuKey,
    );
    if (!exists) {
      result.push({
        menuId: syntheticBaseId - i,
        menuKey: child.menuKey,
        menuName: child.menuName,
        sectionName: child.sectionName,
        routePath: child.routePath,
        routeName: child.routeName || child.menuName,
        sortOrder: child.sortOrder,
        icon: child.icon || resolveMenuIcon(child),
        parentMenuId: parentId,
        isActive: true,
        isSynthetic: true,
        canView: false,
        canCreate: false,
        canEdit: false,
      });
    } else {
      const idx = result.findIndex(
        (r) =>
          r.routePath === child.routePath || r.menuKey === child.menuKey,
      );
      if (idx > -1 && parentId != null) {
        result[idx] = {
          ...result[idx],
          menuName: child.menuKey === "plan_team_all" || child.menuKey === "plan_management_all"
            ? child.menuName
            : result[idx].menuName,
          parentMenuId: result[idx].parentMenuId || parentId,
          sortOrder: child.sortOrder ?? result[idx].sortOrder,
        };
      }
    }
  });
  return result;
}

/** Flat list: parent rows then indented children (same object refs as `rows`). */
export function buildMenuDisplayList(rows, { alreadyMerged = false } = {}) {
  const merged = alreadyMerged
    ? rows
    : mergeKnownMenuChildren(rows.map((r) => normalizeMenuRow({ ...r })));
  const byId = new Map();
  merged.forEach((r) => {
    if (r.menuId != null) byId.set(r.menuId, r);
  });

  const sortMenus = (a, b) =>
    (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
    Number(a.menuId || 0) - Number(b.menuId || 0);

  const parentIdOf = (r) => {
    const pid = r.parentMenuId;
    return pid == null || pid === "" ? null : Number(pid);
  };

  const roots = merged
    .filter((r) => {
      const pid = parentIdOf(r);
      return pid == null || Number.isNaN(pid) || !byId.has(pid);
    })
    .sort(sortMenus);

  const out = [];
  const visit = (node, depth) => {
    out.push({ row: node, depth });
    const nodeId = Number(node.menuId);
    merged
      .filter((c) => parentIdOf(c) === nodeId)
      .sort(sortMenus)
      .forEach((ch) => visit(ch, depth + 1));
  };
  roots.forEach((r) => visit(r, 0));
  return out;
}

const RETIRED_MENU_KEYS = new Set([
  "dashboard_v2",
  "plan_management",
  "plan_management_all",
  "plan_management_team_a",
  "plan_management_team_b",
  "plan_management_team_c",
]);
const RETIRED_MENU_PATHS = new Set([
  "/dashboard-v2",
  "/plan-management",
  "/plan-management/all",
  "/plan-management/team-a",
  "/plan-management/team-b",
  "/plan-management/team-c",
]);

export function isRetiredMenu(row) {
  const key = String(row?.menuKey || row?.menu_key || "").trim();
  const path = String(row?.routePath || row?.route_path || "").trim();
  return RETIRED_MENU_KEYS.has(key) || RETIRED_MENU_PATHS.has(path);
}

export function applyCurrentMenuNames(row) {
  const key = String(row?.menuKey || "").trim();
  const path = String(row?.routePath || "").trim();
  if (key === "plan_management_team" || path === "/test-plan-team") {
    row.menuName = "Plan Management";
  }
  if (key === "management" || path === "/management") {
    row.menuName = "Team Management";
  }
  if (key === "project_overview" || path === "/project-overview") {
    row.menuName = "Project Management";
  }
  if (key === "project_management" || path === "/tables") {
    row.menuName = "Management";
  }
  return row;
}

/** Map role permission payload onto full menu list (includes submenus). */
export function mergeMenuPermissions(allMenus, roleItems, { injectKnown = true } = {}) {
  const permByMenuId = new Map();
  const permByKey = new Map();
  const permByPath = new Map();
  for (const r of roleItems || []) {
    const perm = {
      canView: !!(r.canView ?? r.can_view),
      canCreate: !!(r.canCreate ?? r.can_create),
      canEdit: !!(r.canEdit ?? r.can_edit),
    };
    const id = Number(r.menuId ?? r.menu_id);
    if (Number.isFinite(id) && id > 0) permByMenuId.set(id, perm);
    const key = String(r.menuKey ?? r.menu_key ?? "")
      .trim()
      .toLowerCase();
    const path = String(r.routePath ?? r.route_path ?? "").trim();
    if (key) permByKey.set(key, perm);
    if (path) permByPath.set(path, perm);
  }
  const normalized = (allMenus || []).map((m) => normalizeMenuRow(m));
  const merged = injectKnown
    ? mergeKnownMenuChildren(normalized)
    : dedupeMenuRowsByKey(normalized);
  return merged
    .filter((row) => !isRetiredMenu(row))
    .map((row) => {
      const p =
        permByKey.get(String(row.menuKey || "").trim().toLowerCase()) ||
        permByPath.get(String(row.routePath || "").trim()) ||
        permByMenuId.get(Number(row.menuId));
      return applyCurrentMenuNames({
        ...row,
        canView: p ? p.canView : false,
        canCreate: p ? p.canCreate : false,
        canEdit: p ? p.canEdit : false,
      });
    });
}

/**
 * Build sidebar menu tree: children nested under parent, not duplicated at root.
 * @param {Array} flatRows - menus from API (may include parentMenuId)
 * @param {(row: object) => object} resolveRoute - maps row to vue-router `to`
 */
const HIDDEN_PLAN_MANAGEMENT_KEYS = new Set([
  "plan_management",
  "plan_management_all",
  "plan_management_team_a",
  "plan_management_team_b",
  "plan_management_team_c",
]);
const HIDDEN_PLAN_MANAGEMENT_PATHS = new Set([
  "/plan-management",
  "/plan-management/all",
  "/plan-management/team-a",
  "/plan-management/team-b",
  "/plan-management/team-c",
]);

function isHiddenPlanManagementMenu(row) {
  const key = String(row?.menuKey || "").trim();
  const path = String(row?.routePath || "").trim();
  return HIDDEN_PLAN_MANAGEMENT_KEYS.has(key) || HIDDEN_PLAN_MANAGEMENT_PATHS.has(path);
}

export function buildSidenavMenus(flatRows, resolveRoute) {
  const visibleRows = (flatRows || []).filter((r) => (
    !isHiddenPlanManagementMenu(r)
    && !isRetiredMenu(r)
    && (r.isActive ?? r.is_active ?? true)
    && (r.canView ?? r.can_view ?? true)
    && !r.isSynthetic
  ));
  const normalized = dedupeMenuRowsByKey(visibleRows).map((r) => {
    const row = applyCurrentMenuNames(normalizeMenuRow(r));
    return {
      ...row,
      to: resolveRoute(r),
    };
  });

  const parentIdOf = (r) => {
    const pid = r.parentMenuId;
    return pid == null || pid === "" ? null : Number(pid);
  };

  const byId = new Map();
  normalized.forEach((r) => {
    if (r.menuId != null) byId.set(Number(r.menuId), r);
  });

  const systemParent = normalized.find(
    (r) =>
      r.menuKey === "system_setting" ||
      r.routePath === "/system-setting" ||
      r.menuName === "System Setting",
  );

  const projectOverviewParent = normalized.find(
    (r) =>
      r.menuKey === "project_overview" ||
      r.routePath === "/project-overview" ||
      r.menuName === "Project Overview" ||
      (r.menuName === "Project Management" && r.routePath === "/project-overview"),
  );

  const planManagementParent = normalized.find(
    (r) =>
      r.menuKey === "plan_management" ||
      r.routePath === "/plan-management" ||
      (r.menuName === "Plan Management" && r.routePath === "/plan-management"),
  );

  const planManagementTeamParent = normalized.find(
    (r) =>
      r.menuKey === "plan_management_team" ||
      r.routePath === "/test-plan-team" ||
      r.menuName === "Plan Management Team" ||
      (r.menuName === "Plan Management" && r.routePath === "/test-plan-team"),
  );

  const qaManagementParent = normalized.find(
    (r) =>
      r.menuKey === "qa_management" ||
      r.routePath === "/qa-management" ||
      r.menuName === "QA Management",
  );

  const resolveParentId = (r) => {
    let pid = parentIdOf(r);
    if (pid != null && !Number.isNaN(pid) && byId.has(pid)) return pid;
    const path = String(r.routePath || "");
    if (
      systemParent?.menuId &&
      path.startsWith("/system-setting/") &&
      path !== "/system-setting"
    ) {
      return Number(systemParent.menuId);
    }
    if (
      projectOverviewParent?.menuId &&
      ((path.startsWith("/project-overview/") && path !== "/project-overview") ||
        path === "/tables" ||
        r.menuKey === "project_management")
    ) {
      return Number(projectOverviewParent.menuId);
    }
    if (
      planManagementParent?.menuId &&
      path.startsWith("/plan-management/") &&
      path !== "/plan-management"
    ) {
      return Number(planManagementParent.menuId);
    }
    if (
      planManagementTeamParent?.menuId &&
      path.startsWith("/test-plan-team/") &&
      path !== "/test-plan-team"
    ) {
      return Number(planManagementTeamParent.menuId);
    }
    if (
      qaManagementParent?.menuId &&
      path.startsWith("/qa-management/") &&
      path !== "/qa-management"
    ) {
      return Number(qaManagementParent.menuId);
    }
    return null;
  };

  const isChild = (r) => {
    const pid = resolveParentId(r);
    return pid != null && !Number.isNaN(pid);
  };

  const PLAN_TEAM_CHILD_ORDER = {
    plan_team_all: 1,
    "/test-plan-team/all": 1,
    plan_team_a: 2,
    "/test-plan-team/team-a": 2,
    plan_team_b: 3,
    "/test-plan-team/team-b": 3,
    plan_team_c: 4,
    "/test-plan-team/team-c": 4,
  };
  const planTeamChildRank = (c) =>
    PLAN_TEAM_CHILD_ORDER[c.menuKey] ??
    PLAN_TEAM_CHILD_ORDER[c.routePath] ??
    99;

  const PLAN_PM_CHILD_ORDER = {
    plan_management_all: 1,
    "/plan-management/all": 1,
    plan_management_team_a: 2,
    "/plan-management/team-a": 2,
    plan_management_team_b: 3,
    "/plan-management/team-b": 3,
    plan_management_team_c: 4,
    "/plan-management/team-c": 4,
  };
  const planChildRank = (c) =>
    PLAN_PM_CHILD_ORDER[c.menuKey] ??
    PLAN_PM_CHILD_ORDER[c.routePath] ??
    99;

  const PROJECT_PM_CHILD_ORDER = {
    project_overview_all: 1,
    "/project-overview/all": 1,
    project_overview_my: 2,
    "/project-overview/my": 2,
    project_management: 3,
    "/tables": 3,
  };
  const projectChildRank = (c) =>
    PROJECT_PM_CHILD_ORDER[c.menuKey] ??
    PROJECT_PM_CHILD_ORDER[c.routePath] ??
    99;

  const sortMenus = (a, b) =>
    (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
    Number(a.menuId || 0) - Number(b.menuId || 0);

  const roots = normalized.filter((r) => !isChild(r)).sort(sortMenus);

  return roots.map((root) => {
    const rootId = Number(root.menuId);
    const isProjectParent = Number(projectOverviewParent?.menuId) === rootId;
    const isPlanParent = Number(planManagementParent?.menuId) === rootId;
    const isPlanTeamParent = Number(planManagementTeamParent?.menuId) === rootId;
    const children = normalized
      .filter((c) => resolveParentId(c) === rootId)
      .sort((a, b) => {
        if (isProjectParent) {
          const rank = projectChildRank(a) - projectChildRank(b);
          if (rank) return rank;
        }
        if (isPlanParent) {
          const rank = planChildRank(a) - planChildRank(b);
          if (rank) return rank;
        }
        if (isPlanTeamParent) {
          const rank = planTeamChildRank(a) - planTeamChildRank(b);
          if (rank) return rank;
        }
        return sortMenus(a, b);
      })
      .map((c) => ({
        menuKey: c.menuKey,
        menuName: c.menuName,
        routeName: c.routeName,
        routePath: c.routePath,
        icon: resolveMenuIcon(c),
        to: c.to,
      }));
    const rootOut = { ...root, icon: resolveMenuIcon(root) };
    return children.length ? { ...rootOut, children } : rootOut;
  });
}

/** Permissions to persist — keyed by menu_key / route (includes injected current children). */
export function permissionsForSave(modalRows) {
  const out = new Map();
  for (const r of modalRows || []) {
    if (isRetiredMenu(r)) continue;
    const menuId = Number(r.menuId);
    const menuKey = String(r.menuKey || "").trim() || null;
    const routePath = String(r.routePath || "").trim() || null;
    if (!(menuId > 0) && !menuKey && !routePath) continue;
    const key = String(menuKey || routePath || menuId).toLowerCase();
    out.set(key, {
      menuId: !r.isSynthetic && menuId > 0 ? menuId : undefined,
      menuKey,
      menuName: String(r.menuName || "").trim() || null,
      routePath,
      canView: !!r.canView,
      canCreate: !!r.canCreate,
      canEdit: !!r.canEdit,
    });
  }
  return Array.from(out.values());
}
